/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import axios, { type AxiosError } from 'axios';
import type { TFunction } from 'i18next';
import {
  buildErrorEventParams,
  ERROR_TYPE,
  SSO_ERROR_SOURCE,
  type ErrorEventParams,
} from '../config/analytics';
import { getApiErrorMessage } from './errors';

type SsoErrorData = {
  code?: string;
  message?: string;
  attemptsLeft?: number;
};

export interface ResolvedSsoError {
  /** Localised, user-facing text — what the notification shows. */
  message: string;
  /** Normalised SSO code, when the response carried one. */
  code?: string;
  status?: number;
  description?: string;
}

export function toSsoErrorKey(code: string): string {
  const [head = '', ...rest] = code.toLowerCase().split('_');
  return (
    head + rest.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join('')
  );
}

export function describeSsoError(
  error: unknown,
  t: TFunction,
  fallback: string,
): ResolvedSsoError {
  const data = (error as { response?: { data?: SsoErrorData } } | null)
    ?.response?.data;
  const code = data?.code?.trim().toUpperCase();
  const attemptsLeft = data?.attemptsLeft;
  const status = (error as { response?: { status?: number } } | null)?.response
    ?.status;
  const description = data?.message;

  // Product supplies one attempts-left string per language rather than plural
  // forms, so the count lives in a sibling key instead of i18next plurals,
  // whose suffixes differ per language. `defaultValue: ''` lets a missing key
  // fall through to the server message rather than rendering the key.
  const key = code ? toSsoErrorKey(code) : '';
  const localised = key
    ? (attemptsLeft
        ? t(`ssoErrors.${key}WithAttempts`, {
            defaultValue: '',
            count: attemptsLeft,
          })
        : '') || t(`ssoErrors.${key}`, { defaultValue: '' })
    : '';

  return {
    message: localised || getApiErrorMessage(error, fallback),
    code,
    status,
    description,
  };
}

// Native sign-in spec codes; unlisted codes fall back to generic copy.
export const SOCIAL_AUTH_ERROR_CODES = [
  'INVALID_CREDENTIALS',
  'INVALID_USERNAME',
  'USER_NEW',
  'USER_NOT_ACTIVATED',
  'USER_LOCKED',
  'USER_TERMINATED',
  'USER_INACTIVE',
  'USER_SCHEDULED_FOR_DELETION',
  'TOO_MANY_REQUESTS',
  'OTP_COOLDOWN_LIMIT_REACHED',
  'FACEBOOK_ACCOUNT_WITH_NO_EMAIL',
  'MOBILE_NUMBER_LINKED_TO_ANOTHER_ACCOUNT',
  'METHOD_IS_NOT_ALLOWED',
] as const;

const SOCIAL_AUTH_ERROR_KEYS: Record<string, string> = {
  INVALID_CREDENTIALS: 'invalidCredentials',
  INVALID_USERNAME: 'invalidUsername',
  USER_NEW: 'userNew',
  USER_NOT_ACTIVATED: 'userNotActivated',
  USER_LOCKED: 'userLocked',
  USER_TERMINATED: 'userTerminated',
  USER_INACTIVE: 'userInactive',
  USER_SCHEDULED_FOR_DELETION: 'userScheduledForDeletion',
  TOO_MANY_REQUESTS: 'tooManyRequests',
  OTP_COOLDOWN_LIMIT_REACHED: 'otpCooldownLimitReached',
  FACEBOOK_ACCOUNT_WITH_NO_EMAIL: 'facebookAccountWithNoEmail',
  MOBILE_NUMBER_LINKED_TO_ANOTHER_ACCOUNT: 'mobileNumberLinkedToAnotherAccount',
  METHOD_IS_NOT_ALLOWED: 'methodIsNotAllowed',
};

const toAxiosError = (error: unknown): AxiosError | null =>
  axios.isAxiosError(error) ? error : null;

export function isSsoNetworkError(error: unknown): boolean {
  const axiosError = toAxiosError(error);
  if (!axiosError || axiosError.response) {
    return false;
  }

  return (
    axiosError.code === 'ERR_NETWORK' ||
    axiosError.code === 'ECONNABORTED' ||
    axiosError.code === 'ETIMEDOUT' ||
    /network error/i.test(axiosError.message)
  );
}

export function isSsoParseError(error: unknown): boolean {
  if (error instanceof SyntaxError) return true;

  const axiosError = toAxiosError(error);
  const message =
    error instanceof Error ? error.message : (axiosError?.message ?? '');

  return (
    axiosError?.code === 'ERR_BAD_RESPONSE' ||
    /parse|unexpected token/i.test(message)
  );
}

// Maps social-exchange API errors to localized banner copy.
export function describeSocialAuthError(
  error: unknown,
  t: TFunction,
  fallback: string,
): ResolvedSsoError {
  if (isSsoNetworkError(error)) {
    return {
      message: t('socialAuthErrors.noInternet', { defaultValue: fallback }),
    };
  }

  if (isSsoParseError(error)) {
    return {
      message: t('socialAuthErrors.dataParsingFailed', {
        defaultValue: fallback,
      }),
    };
  }

  const data = (error as { response?: { data?: SsoErrorData } } | null)
    ?.response?.data;
  const code = data?.code?.trim().toUpperCase();
  const oops = t('socialAuthErrors.oops', { defaultValue: fallback });

  if (!code) {
    return { message: oops };
  }

  const key = SOCIAL_AUTH_ERROR_KEYS[code];
  if (!key) {
    return { message: oops, code };
  }

  const localised = t(`socialAuthErrors.${key}`, { defaultValue: '' });
  return { message: localised || oops, code };
}

/** `errors` params for a failed SSO request: `<status>|<SSO code>|<shown message>`. */
export function buildSsoErrorEventParams(
  error: unknown,
  message: string,
  screenName: string,
): ErrorEventParams {
  const response = (
    error as {
      response?: { status?: number; data?: SsoErrorData };
    } | null
  )?.response;

  return buildErrorEventParams({
    type: response
      ? ERROR_TYPE.api
      : isSsoNetworkError(error)
        ? ERROR_TYPE.network
        : ERROR_TYPE.client,
    code:
      response?.status ??
      toAxiosError(error)?.code ??
      (error instanceof Error ? error.name : undefined),
    description: response?.data?.code ?? response?.data?.message,
    message,
    screenName,
    ...SSO_ERROR_SOURCE,
  });
}
